import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Plus, Trash2, ChevronUp, ChevronDown, ChevronRight, ChevronDown as ChevronDownIcon,
  GripVertical
} from 'lucide-react';
import { RichBlockEditor, ContentBlock, ContentSection } from './RichBlockEditor';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

interface SectionEditorProps {
  sections: ContentSection[];
  onChange: (sections: ContentSection[]) => void;
  className?: string;
  allowAddSection?: boolean;
  sectionLabel?: string;
}

const generateId = () => Math.random().toString(36).substr(2, 9);

export function SectionEditor({ 
  sections, 
  onChange, 
  className,
  allowAddSection = true,
  sectionLabel = 'Section'
}: SectionEditorProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(sections.map(s => s.id))
  );

  const toggleSection = (id: string) => {
    const updated = new Set(expandedSections);
    if (updated.has(id)) {
      updated.delete(id);
    } else {
      updated.add(id);
    }
    setExpandedSections(updated);
  };

  const addSection = () => {
    const newSection: ContentSection = {
      id: generateId(),
      title: `New ${sectionLabel}`,
      blocks: [],
    };
    onChange([...sections, newSection]);
    setExpandedSections(prev => new Set([...prev, newSection.id]));
  };

  const updateSection = (index: number, updates: Partial<ContentSection>) => {
    const updated = [...sections];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  const removeSection = (index: number) => {
    onChange(sections.filter((_, i) => i !== index));
  };

  const moveSection = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;
    
    const updated = [...sections];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onChange(updated);
  };

  return (
    <div className={cn('space-y-4', className)}>
      {sections.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
          <p className="mb-4">No sections yet. Add your first section to get started.</p>
          {allowAddSection && (
            <Button variant="outline" onClick={addSection}>
              <Plus className="h-4 w-4 mr-2" />
              Add {sectionLabel}
            </Button>
          )}
        </div>
      ) : (
        <>
          {sections.map((section, index) => (
            <Card key={section.id} className="overflow-hidden">
              <Collapsible
                open={expandedSections.has(section.id)}
                onOpenChange={() => toggleSection(section.id)}
              >
                <CardHeader className="py-3 px-4 bg-muted/50">
                  <div className="flex items-center gap-3">
                    <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
                    
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSection(index, 'up');
                        }}
                        disabled={index === 0}
                      >
                        <ChevronUp className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSection(index, 'down');
                        }}
                        disabled={index === sections.length - 1}
                      >
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </div>

                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-6 w-6">
                        {expandedSections.has(section.id) ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </Button>
                    </CollapsibleTrigger>

                    <Input
                      value={section.title}
                      onChange={(e) => updateSection(index, { title: e.target.value })}
                      className="flex-1 h-8 font-medium"
                      placeholder={`${sectionLabel} title...`}
                      onClick={(e) => e.stopPropagation()}
                    />

                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeSection(index);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>

                <CollapsibleContent>
                  <CardContent className="pt-4">
                    <RichBlockEditor
                      blocks={section.blocks}
                      onChange={(blocks) => updateSection(index, { blocks })}
                      placeholder={`Add content to this ${sectionLabel.toLowerCase()}...`}
                    />
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          ))}
        </>
      )}

      {allowAddSection && sections.length > 0 && (
        <Button variant="outline" onClick={addSection} className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          Add {sectionLabel}
        </Button>
      )}
    </div>
  );
}
